import {
  Button,
  Checkbox,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useEffect, useState } from 'react';
import useSound from 'use-sound';
import { useQueryClient } from 'react-query';
import { INVOICES_KEY, INVOICE_ROW_STATUS_KEY } from '../../api/hooks/cache-keys';
import { useGetInvoiceDetails, useGetInvoiceRowStatus } from '../../api/hooks/sales';
import { toPersianDate, toPersianNumber } from '../../utils/helpers';
import success from '../../sounds/success.wav';
import { useDeliverInvoice, useExportDevice } from '../../api/hooks/inventory';

export default function InvoiceDetails({ id }) {
  const queryClient = useQueryClient();
  const { data: invoice } = useGetInvoiceDetails(id);
  const { data: rowStatus } = useGetInvoiceRowStatus(id);
  const exportDevice = useExportDevice();
  const deliverInoive = useDeliverInvoice(id);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [imei, setImei] = useState('');

  const [playSuccessSound] = useSound(success);

  async function addDeviceToInvoice() {
    try {
      await exportDevice.mutateAsync(
        { rowId: selected, imei },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries(INVOICE_ROW_STATUS_KEY(id));
            setImei('');
            setError(false);
            playSuccessSound();
          },
          onError: () => {
            setError(true);
          },
        }
      );
    } catch (e) {
      setError(true);
    }
  }

  async function handleDeliverInvoice() {
    try {
      await deliverInoive.mutateAsync();
      await queryClient.invalidateQueries(INVOICES_KEY());
      setError(false);
    } catch (e) {
      setError(true);
    }
  }

  useEffect(() => {
    if (imei.length === 15) {
      addDeviceToInvoice();
    }
  }, [imei]);

  const columns = [
    {
      id: 'id',
      label: 'مدل',
      minWidth: 20,
      format: (id) => <Checkbox checked={id === selected} size="small" />,
    },
    {
      id: 'model',
      label: 'مدل',
      minWidth: 100,
      format: (model) => `${model.name} (${model.ram}/${model.memory})`,
    },
    { id: 'color', label: 'رنگ', minWidth: 50, format: (color) => color.name },
    { id: 'amount', label: 'تعداد', minWidth: 30, format: toPersianNumber },
    {
      id: 'count',
      label: 'فعلی',
      minWidth: 30,
      format: (count) => <Typography color="textSecondary">{toPersianNumber(count)}</Typography>,
    },
  ];

  return (
    <Container maxWidth="md">
      <Typography variant="h6">فاکتور شماره {toPersianNumber(invoice?.number)}</Typography>
      <Grid container sx={{ py: 2 }} spacing={2}>
        <Grid item xs={5}>
          <TextField
            value={invoice?.proforma?.seller?.name}
            size="small"
            label="فروشنده"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={5}>
          <TextField
            value={invoice?.proforma?.buyer?.name}
            size="small"
            label="خریدار"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            value={toPersianDate(invoice?.proforma?.createdAt)}
            size="small"
            label="تاریخ صدور"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={8} justifyContent="start">
          <Stack direction="row-reverse" spacing={2}></Stack>
        </Grid>
        <Grid xs={12} lg={6}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth, fontWeight: '600' }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice?.proforma?.rows
                  ?.map((item) =>
                    Object.assign(
                      {},
                      item,
                      rowStatus?.find((s) => s.row === item.id)
                    )
                  )
                  ?.map((row) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={`${row.model.name}-${row.id}`}
                        onClick={() => setSelected(row.id)}
                        selected={selected === row.id}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align} size="small">
                              {column.format ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid
          container
          xs={12}
          lg={6}
          spacing={2}
          sx={{ pl: { xs: 0, lg: 2 }, pt: { xs: 2, lg: 0 } }}
        >
          <Grid item xs={12} lg={6}>
            <TextField
              value={invoice?.proforma?.rows?.find((r) => r.id === selected)?.model?.name}
              size="small"
              label="مدل"
              focused={selected !== null}
              fullWidth
              inputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              value={invoice?.proforma?.rows?.find((r) => r.id === selected)?.color?.name}
              size="small"
              label="رنگ"
              focused={selected !== null}
              fullWidth
              inputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              label="IMEI 1"
              type="numeral"
              onChange={(e) => setImei(e.target.value)}
              value={imei}
              fullWidth
              disabled={selected === null}
              error={error}
              InputProps={{
                endAdornment:
                  imei.length > 0 ? (
                    <IconButton onClick={() => setImei('')} sx={{ p: 0 }}>
                      <HighlightOffIcon position="end" />
                    </IconButton>
                  ) : (
                    <QrCodeScannerIcon position="end" />
                  ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={6} md={10}></Grid>
        <Grid item xs={6} md={2}>
          <Button
            variant="contained"
            fullWidth
            disabled={invoice.delivered}
            onClick={handleDeliverInvoice}
          >
            تکمیل
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
}
